
import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { DataTable } from "@/components/shared/DataTable";
import { FormDialog } from "@/components/shared/FormDialog";
import { OrderForm, OrderFormValues } from "@/components/orders/OrderForm";
import { getOrderColumns } from "@/components/orders/OrderColumns";
import { useOrders } from "@/hooks/useOrders";
import { SearchBar } from "@/components/shared/SearchBar";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";

const Orders = () => {
  const {
    orders,
    customers,
    factories,
    tailors,
    categories,
    orderStatuses,
    loading,
    createOrder,
    updateOrder,
    deleteOrder
  } = useOrders();
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [filteredOrders, setFilteredOrders] = useState<any[]>([]);
  
  const orderColumns = getOrderColumns();

  // Update filtered orders when orders data changes
  useEffect(() => {
    setFilteredOrders(orders);
  }, [orders]);

  const handleSearch = (query: string) => {
    if (!query) {
      setFilteredOrders(orders);
      return;
    }

    const normalizedQuery = query.toLowerCase();
    const filtered = orders.filter(order => {
      return (
        order.order_number?.toLowerCase().includes(normalizedQuery) ||
        order.tailor?.profile?.firstName?.toLowerCase().includes(normalizedQuery) ||
        order.tailor?.profile?.lastName?.toLowerCase().includes(normalizedQuery) ||
        order.tailor?.email?.toLowerCase().includes(normalizedQuery) ||
        order.customer?.customer_name?.toLowerCase().includes(normalizedQuery) ||
        order.customer?.customer_email?.toLowerCase().includes(normalizedQuery) ||
        order.customer?.customer_phone?.toLowerCase().includes(normalizedQuery) ||
        order.status?.toLowerCase().includes(normalizedQuery) ||
        String(order.total_amount).includes(normalizedQuery) ||
        order.factory?.name?.toLowerCase().includes(normalizedQuery)
      );
    });
    setFilteredOrders(filtered);
  };

  const handleSubmit = async (values: OrderFormValues) => {
    try {
      let success;
      
      if (editingOrder) {
        success = await updateOrder(editingOrder.id, values);
      } else {
        success = await createOrder(values);
      }

      if (success) {
        setOpenDialog(false);
        setEditingOrder(null);
      }
    } catch (error: any) {
      console.error("Error submitting order:", error);
    }
  };

  const handleEdit = (order: any) => {
    setEditingOrder(order);
    setOpenDialog(true);
  };

  const handleDelete = async (order: any) => {
    return await deleteOrder(order.id);
  };

  const handleAddNew = () => {
    setEditingOrder(null);
    setOpenDialog(true);
  };

  const defaultValues = editingOrder ? {
    order_number: editingOrder.order_number || "",
    customer_id: editingOrder.customer_id || "none",
    factory_id: editingOrder.factory_id || "none",
    tailor_id: editingOrder.tailor_id || "none",
    category_id: editingOrder.product_id || "none",
    status: editingOrder.status || "pending",
    total_amount: editingOrder.total_amount || 0,
    notes: editingOrder.notes || "",
  } : {
    order_number: `ORD-${Math.floor(1000 + Math.random() * 9000)}`,
    customer_id: "none",
    factory_id: "none",
    tailor_id: "none",
    category_id: "none",
    status: "pending",
    total_amount: 0,
    notes: "",
  };

  return (
    <div className="space-y-8">
      <Card className="glass-card p-6 animate-fadeIn">
        <h2 className="text-2xl font-semibold mb-4">Order Management</h2>
        <p className="text-muted-foreground">
          Create and manage orders, assign to tailors and factories, and track status.
        </p>
      </Card>

      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Orders</h3>
          <div className="flex items-center gap-2">
            <SearchBar
              placeholder="Search orders..."
              onSearch={handleSearch}
              className="w-auto"
            />
            {/* <Button onClick={handleAddNew} className="flex items-center gap-2">
              <PlusCircle className="h-4 w-4" />
              Add Order
            </Button> */}
          </div>
        </div>
        
        <DataTable
          data={filteredOrders}
          columns={orderColumns}
          onAdd={handleAddNew}
          onEdit={handleEdit}
          onDelete={handleDelete}
          title="Orders"
          itemName="Order"
          isLoading={loading}
        />
      </Card>

      <FormDialog 
        title={editingOrder ? "Edit Order" : "Create New Order"} 
        open={openDialog} 
        onOpenChange={setOpenDialog}
      >
        <OrderForm
          onSubmit={handleSubmit}
          customers={customers}
          factories={factories}
          tailors={tailors}
          categories={categories}
          orderStatuses={orderStatuses}
          defaultValues={defaultValues}
          isEditing={!!editingOrder}
          onCancel={() => setOpenDialog(false)}
        />
      </FormDialog>
    </div>
  );
};

export default Orders;
